import React, { useRef, useState, useCallback, useEffect } from "react";
import { ZoomIn, ZoomOut, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, RefreshCw, Loader2, AlertCircle } from "lucide-react";
import { getFaceLandmarker, solveOverlay } from "@/lib/faceLandmarker";

const PUPIL_BIAS = 0.08;

export function TryOnCanvas({ photoUrl, glassesUrl, product }) {
  const [nudge, setNudge] = useState({ x: 0, y: 0, scale: 1 });
  const [placement, setPlacement] = useState(null);
  const [state, setState] = useState("detecting");
  const dragRef = useRef(null);
  const containerRef = useRef(null);
  const photoRef = useRef(null);
  const overlayRef = useRef(null);

  const detect = useCallback(async () => {
    const box = containerRef.current;
    const photo = photoRef.current;
    if (!box || !photo) return;
    setState("detecting");
    try {
      const landmarker = await getFaceLandmarker("IMAGE");
      if (!photo.complete) await new Promise((res) => { photo.onload = res; });
      const result = landmarker.detect(photo);
      const landmarks = result?.faceLandmarks?.[0];
      if (!landmarks) {
        setState("no-face");
        setPlacement({ x: box.clientWidth / 2, y: box.clientHeight * 0.42, width: box.clientWidth * 0.5, angle: 0 });
        return;
      }
      setPlacement(solveOverlay(
        landmarks,
        { w: photo.naturalWidth, h: photo.naturalHeight },
        { w: box.clientWidth, h: box.clientHeight },
        product,
      ));
      setState("placed");
    } catch {
      setState("error");
      setPlacement({ x: box.clientWidth / 2, y: box.clientHeight * 0.42, width: box.clientWidth * 0.5, angle: 0 });
    }
  }, [product]);

  useEffect(() => {
    setNudge({ x: 0, y: 0, scale: 1 });
    detect();
  }, [photoUrl, detect]);

  // Placement is in box pixels, so it has to be re-solved whenever the canvas resizes.
  useEffect(() => {
    const box = containerRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    let timer;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(detect, 150);
    });
    observer.observe(box);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [detect]);

  const onPointerDown = useCallback((e) => {
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    dragRef.current = { startX, startY, origX: nudge.x, origY: nudge.y };
  }, [nudge.x, nudge.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    setNudge((s) => ({
      ...s,
      x: dragRef.current.origX + (clientX - dragRef.current.startX),
      y: dragRef.current.origY + (clientY - dragRef.current.startY),
    }));
  }, []);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);
  const move = (dx, dy) => setNudge((s) => ({ ...s, x: s.x + dx, y: s.y + dy }));
  const zoom = (d) => setNudge((s) => ({ ...s, scale: Math.max(0.6, Math.min(1.8, s.scale + d)) }));
  const reset = () => setNudge({ x: 0, y: 0, scale: 1 });

  const width = placement ? placement.width * nudge.scale : 0;
  const aspect = overlayRef.current?.naturalWidth ? overlayRef.current.naturalHeight / overlayRef.current.naturalWidth : 0.4;

  const btn = "border border-zinc-200 text-zinc-700 hover:border-teal-600 hover:text-teal-700 w-10 h-10 rounded-full flex items-center justify-center transition-colors";

  return (
    <div>
      <div
        ref={containerRef}
        className="relative w-full aspect-[3/4] max-w-md mx-auto bg-paper border border-zinc-200 rounded-2xl overflow-hidden touch-none select-none"
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
        data-testid="tryon-canvas"
      >
        <img ref={photoRef} src={photoUrl} alt="Your photo" className="absolute inset-0 w-full h-full object-cover" draggable={false} onLoad={detect} />
        {placement && (
          <img
            ref={overlayRef}
            src={glassesUrl}
            alt="Glasses overlay"
            onMouseDown={onPointerDown}
            onTouchStart={onPointerDown}
            className="absolute cursor-grab active:cursor-grabbing"
            style={{
              width: `${width}px`,
              left: `${placement.x + nudge.x}px`,
              top: `${placement.y + nudge.y + width * aspect * PUPIL_BIAS}px`,
              transform: `translate(-50%, -50%) rotate(${placement.angle}deg)`,
            }}
            draggable={false}
            data-testid="tryon-overlay-image"
          />
        )}
        {state === "detecting" && (
          <div className="absolute inset-0 flex items-center justify-center bg-charcoal/50 text-white" data-testid="tryon-detecting">
            <span className="inline-flex items-center gap-2 text-xs font-heading uppercase tracking-wide"><Loader2 size={14} className="animate-spin" /> Finding your face…</span>
          </div>
        )}
        {state === "no-face" && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-charcoal/85 text-white text-[11px] font-heading uppercase tracking-wide px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="tryon-no-face-hint">
            <AlertCircle size={12} /> No face found — position the frames yourself
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-2 mt-6" data-testid="tryon-controls">
        <button onClick={() => move(-8, 0)} className={btn} aria-label="Move left" data-testid="tryon-move-left"><ArrowLeft size={15} /></button>
        <button onClick={() => move(8, 0)} className={btn} aria-label="Move right" data-testid="tryon-move-right"><ArrowRight size={15} /></button>
        <button onClick={() => move(0, -8)} className={btn} aria-label="Move up" data-testid="tryon-move-up"><ArrowUp size={15} /></button>
        <button onClick={() => move(0, 8)} className={btn} aria-label="Move down" data-testid="tryon-move-down"><ArrowDown size={15} /></button>
        <button onClick={() => zoom(0.05)} className={btn} aria-label="Increase size" data-testid="tryon-zoom-in"><ZoomIn size={15} /></button>
        <button onClick={() => zoom(-0.05)} className={btn} aria-label="Decrease size" data-testid="tryon-zoom-out"><ZoomOut size={15} /></button>
        <button onClick={reset} className={btn} aria-label="Reset placement" data-testid="tryon-reset"><RefreshCw size={15} /></button>
      </div>
      <p className="text-center text-xs text-zinc-400 mt-3">
        {state === "placed" ? "Placed automatically from your photo and this frame's real measurements. Drag to fine-tune." : "Drag the frames to reposition, or use the controls above."}
      </p>
    </div>
  );
}
